/**
 * Procedure URL registry - generated from gm-content.json slugs.
 * Pattern: /{country}/{procedure-path} e.g. /in/st-visa → in-student
 * Regenerate: python scripts/gen-procedure-routes.py
 */

/** @type {Record<string, string>} content slug → URL path (no leading slash) */
export const SLUG_TO_ROUTE = {
  'br-family': 'br/family',
  'br-mercosur': 'br/mercosur',
  'br-perm': 'br/perm',
  'br-study': 'br/st-visa',
  'br-study-vitem-i': 'br/st-vitem-i',
  'br-work': 'br/work-visa',
  'br-work-residence': 'br/work-residence',
  'br-work-vitem-ix': 'br/work-vitem-ix',
  'es-arraigo': 'es/arraigo',
  'es-bluecard': 'es/bluecard',
  'es-employed': 'es/employed',
  'es-employed-renewal': 'es/employed-renewal',
  'es-family': 'es/family',
  'es-family-renewal': 'es/family-renewal',
  'es-longterm': 'es/longterm',
  'es-student': 'es/st-visa',
  'in-family': 'in/family',
  'in-project': 'in/project',
  'in-student': 'in/st-visa',
  'in-work': 'in/work-visa',
  'it-bluecard': 'it/bluecard',
  'it-employed': 'it/employed',
  'it-family': 'it/family',
  'it-ict': 'it/ict',
  'it-longterm': 'it/longterm',
  'it-student': 'it/st-visa',
  'ma-family': 'ma/family',
  'ma-residence': 'ma/residence',
  'ma-student': 'ma/st-visa',
  'ma-work': 'ma/work-visa',
  'ph-family': 'ph/family',
  'ph-perm': 'ph/perm',
  'ph-student': 'ph/st-visa',
  'ph-work': 'ph/work-visa',
  'pt-bluecard': 'pt/bluecard',
  'pt-family': 'pt/family',
  'pt-highly-qualified': 'pt/highly-qualified',
  'pt-student': 'pt/st-visa',
  'pt-work-independent': 'pt/work-independent',
  'pt-work-subordinate': 'pt/work-subordinate',
  'us-aos': 'us/aos',
  'us-eb': 'us/eb',
  'us-f1': 'us/st-visa',
  'us-family-ir': 'us/family-ir',
  'us-k1': 'us/k1',
  'us-l1': 'us/l1',
  'us-o1': 'us/o1',
  'us-work': 'us/work-visa',
};

/** @type {Record<string, string>} URL path → content slug */
export const ROUTE_TO_SLUG = {
  'br/family': 'br-family',
  'br/mercosur': 'br-mercosur',
  'br/perm': 'br-perm',
  'br/st-visa': 'br-study',
  'br/st-vitem-i': 'br-study-vitem-i',
  'br/work-residence': 'br-work-residence',
  'br/work-visa': 'br-work',
  'br/work-vitem-ix': 'br-work-vitem-ix',
  'es/arraigo': 'es-arraigo',
  'es/bluecard': 'es-bluecard',
  'es/employed': 'es-employed',
  'es/employed-renewal': 'es-employed-renewal',
  'es/family': 'es-family',
  'es/family-renewal': 'es-family-renewal',
  'es/longterm': 'es-longterm',
  'es/st-visa': 'es-student',
  'in/family': 'in-family',
  'in/project': 'in-project',
  'in/st-visa': 'in-student',
  'in/work-visa': 'in-work',
  'it/bluecard': 'it-bluecard',
  'it/employed': 'it-employed',
  'it/family': 'it-family',
  'it/ict': 'it-ict',
  'it/longterm': 'it-longterm',
  'it/st-visa': 'it-student',
  'ma/family': 'ma-family',
  'ma/residence': 'ma-residence',
  'ma/st-visa': 'ma-student',
  'ma/work-visa': 'ma-work',
  'ph/family': 'ph-family',
  'ph/perm': 'ph-perm',
  'ph/st-visa': 'ph-student',
  'ph/work-visa': 'ph-work',
  'pt/bluecard': 'pt-bluecard',
  'pt/family': 'pt-family',
  'pt/highly-qualified': 'pt-highly-qualified',
  'pt/st-visa': 'pt-student',
  'pt/work-independent': 'pt-work-independent',
  'pt/work-subordinate': 'pt-work-subordinate',
  'us/aos': 'us-aos',
  'us/eb': 'us-eb',
  'us/family-ir': 'us-family-ir',
  'us/k1': 'us-k1',
  'us/l1': 'us-l1',
  'us/o1': 'us-o1',
  'us/st-visa': 'us-f1',
  'us/work-visa': 'us-work',
};

export const PROCEDURE_ROUTE_PATHS = Object.keys(ROUTE_TO_SLUG);

function toCanonicalSlug(country, procedurePath) {
  const c = String(country || '').trim();
  const p = String(procedurePath || '').trim();
  if (!c || !p) return null;
  return `${c.toUpperCase()}-${p}`;
}

function parseCanonicalSlug(slug) {
  const clean = String(slug || '').trim();
  const match = clean.match(/^([A-Za-z]{2})-(.+)$/);
  if (!match) return null;
  return {
    country: match[1].toLowerCase(),
    procedurePath: match[2].toLowerCase(),
  };
}

export function resolveProcedureSlug(country, procedurePath) {
  const c = String(country || '').toLowerCase();
  const key = `${c}/${String(procedurePath || '').toLowerCase()}`;
  return ROUTE_TO_SLUG[key] || toCanonicalSlug(country, procedurePath);
}

export function getProcedureBasePath(slug) {
  const route = SLUG_TO_ROUTE[slug];
  if (route) return `/${route}`;
  const parsed = parseCanonicalSlug(slug);
  return parsed ? `/${parsed.country}/${parsed.procedurePath}` : null;
}

export function isProcedureLandingPath(pathname) {
  const clean = String(pathname || '').replace(/\/+$/, '');
  const parts = clean.split('/').filter(Boolean);
  if (parts.length !== 2) return false;
  const key = `${parts[0].toLowerCase()}/${parts[1].toLowerCase()}`;
  if (ROUTE_TO_SLUG[key]) return true;
  return Boolean(toCanonicalSlug(parts[0], parts[1]));
}

/**
 * True for procedure sub-pages (qualify, registro, success, etc.)
 * e.g. /pt/work-subordinate/qualify → true
 */
export function isProcedureSubPath(pathname) {
  const clean = String(pathname || '').replace(/\/+$/, '');
  const parts = clean.split('/').filter(Boolean);
  if (parts.length < 3) return false;
  const key = `${parts[0].toLowerCase()}/${parts[1].toLowerCase()}`;
  if (ROUTE_TO_SLUG[key]) return true;
  return Boolean(toCanonicalSlug(parts[0], parts[1]));
}

export function getProcedureBasePathFromLocation(pathname) {
  const clean = String(pathname || '').replace(/\/+$/, '');
  const parts = clean.split('/').filter(Boolean);
  if (parts.length < 2) return null;
  const key = `${parts[0].toLowerCase()}/${parts[1].toLowerCase()}`;
  if (ROUTE_TO_SLUG[key]) return `/${key}`;
  const canonical = toCanonicalSlug(parts[0], parts[1]);
  return canonical ? `/${parts[0].toLowerCase()}/${parts[1].toLowerCase()}` : null;
}
